import type { FormDraftView, FormField } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, Download, FileText, Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { callAction } from "../api";
import { ModeBadge } from "../components/ModeBadge";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, CardHeader, ErrorState, LoadingState, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { shortDate } from "../theme";

type Row = Record<string, unknown>;

function getPath(data: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, k) => (acc && typeof acc === "object" ? (acc as Record<string, unknown>)[k] : undefined), data);
}

export function FormDraftScreen() {
  const { draftId = "" } = useParams();
  const t = useT();
  const { locale } = useLocale();
  const qc = useQueryClient();
  const { toast } = useToast();
  const key = ["form-draft", draftId];
  const q = useQuery({ queryKey: key, queryFn: async () => (await callAction<"agency.form.draft.get", FormDraftView>("agency.form.draft.get", { draftId })).result, enabled: !!draftId });
  const [patch, setPatch] = useState<Record<string, unknown>>({});
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => { setPatch({}); }, [draftId]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ["form-drafts"] });
    if (q.data) qc.invalidateQueries({ queryKey: ["customer", q.data.draft.customerId] });
  };
  const save = useMutation({
    mutationFn: () => callAction<"agency.form.draft.update", FormDraftView>("agency.form.draft.update", { draftId, patch, expectedVersion: q.data?.draft.version }),
    onSuccess: (r) => { setPatch({}); invalidate(); toast({ title: t("forms.saved"), body: t("forms.unanswered").replace("{n}", String(r.result.draft.missing.length)), tone: "success" }); },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const generate = useMutation({
    mutationFn: async (allowMissing: boolean) => {
      if (Object.keys(patch).length) await callAction<"agency.form.draft.update", FormDraftView>("agency.form.draft.update", { draftId, patch, expectedVersion: q.data?.draft.version });
      return callAction<"agency.form.draft.generate", FormDraftView>("agency.form.draft.generate", { draftId, allowMissing });
    },
    onSuccess: () => { setPatch({}); invalidate(); setPreviewOpen(true); toast({ title: t("forms.generated"), tone: "success" }); },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });

  const value = (path: string): unknown => (path in patch ? patch[path] : q.data ? getPath(q.data.draft.data, path) : undefined);
  const set = (path: string, v: unknown) => setPatch((p) => ({ ...p, [path]: v }));

  const fields = useMemo(() => (q.data ? q.data.def.sections.flatMap((s) => s.fields) : []), [q.data]);
  const missingNow = useMemo(() => fields.filter((f) => f.required && blank(value(f.path))).map((f) => f.path), [fields, patch, q.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (q.isLoading) return <LoadingState />;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const { draft, def, policy, holder, document, downloadUrl } = q.data;
  const dirty = Object.keys(patch).length > 0;
  const demo = !document || document.executionMode !== "live";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="mr-auto min-w-0">
          <p className="text-sm text-ink/60"><Link to="/forms" className="hover:underline">{t("forms.title")}</Link> · <Link to={`/customers/${draft.customerId}`} className="hover:underline">{q.data.customerName}</Link>{policy ? ` · ${policy.carrier} ${policy.policyNumber}` : ""}{holder ? ` · ${holder.name}` : ""}</p>
          <h1 className="text-2xl font-bold">{def.workflow[locale]}</h1>
          <p className="text-sm text-ink/60">{t("forms.targetForm")}: ACORD {draft.formNumber} · {t("forms.intendedEdition")}: {draft.intendedEdition} · {t("forms.state")}: {draft.state} · {t("forms.version").replace("{n}", String(draft.version))} · {shortDate(draft.updatedAt, locale)}</p>
        </div>
        <Badge tone={draft.status === "generated" ? "green" : "amber"}>{t(`forms.status.${draft.status}`)}</Badge>
        <Badge tone="blue">{t("forms.prefilledCount").replace("{n}", String(draft.prefilled.length))}</Badge>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
        <div className="space-y-4">
          {def.sections.map((section) => (
            <Card key={section.id}>
              <CardHeader title={section.title[locale]} />
              <div className="px-5 pb-5 grid gap-3 sm:grid-cols-2">
                {section.fields.map((f) => (
                  <FieldEditor key={f.path} field={f} value={value(f.path)} onChange={(v) => set(f.path, v)} prefilled={draft.prefilled.includes(f.path) && !(f.path in patch)} missing={missingNow.includes(f.path)} locale={locale} />
                ))}
              </div>
            </Card>
          ))}
        </div>

        <div className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          <Card className="p-4 space-y-3">
            <div className="flex flex-col gap-2">
              <Button onClick={() => save.mutate()} disabled={!dirty} loading={save.isPending}><Save className="h-4 w-4" />{t("forms.save")}</Button>
              <Button variant="secondary" onClick={() => generate.mutate(false)} disabled={missingNow.length > 0} loading={generate.isPending}><FileText className="h-4 w-4" />{draft.status === "generated" ? t("forms.regenerate") : t("forms.generate")}</Button>
              {missingNow.length ? <Button variant="outline" size="sm" onClick={() => generate.mutate(true)} loading={generate.isPending}>{t("forms.generateWithMissing")}</Button> : null}
            </div>
            <ModeBadge mode={document?.executionMode ?? "fixture"} long />
            {demo ? <p className="rounded-xl bg-accent/15 p-2 text-xs text-amber-900">{t("forms.demoBanner")}</p> : null}
          </Card>

          <Card className="p-4">
            <p className="font-bold inline-flex items-center gap-2">{missingNow.length ? <AlertCircle className="h-4 w-4 text-amber-700" /> : <CheckCircle2 className="h-4 w-4 text-primary-dark" />}{t("forms.missingTitle")} {missingNow.length ? <Badge tone="amber">{missingNow.length}</Badge> : null}</p>
            <p className="text-xs text-ink/55 mt-1">{missingNow.length ? t("forms.missingBody") : t("forms.allAnswered")}</p>
            {missingNow.length ? (
              <ul className="mt-2 space-y-1">
                {fields.filter((f) => missingNow.includes(f.path)).map((f) => (
                  <li key={f.path}>
                    <button className="w-full text-left rounded-lg px-2 min-h-[36px] text-sm hover:bg-ink/5" onClick={() => document_scroll(f.path)}>{f.label[locale]}</button>
                  </li>
                ))}
              </ul>
            ) : null}
          </Card>

          {document ? (
            <Card className="p-4 space-y-2">
              <p className="font-bold">{document.title}</p>
              <p className="text-xs text-ink/55">{t("forms.documentId")}: <span className="font-mono">{document.id}</span></p>
              <p className="text-xs text-ink/55">{t("forms.draftRef")}: <span className="font-mono">{document.sourceRecord}</span> · {shortDate(document.createdAt, locale)}</p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setPreviewOpen((v) => !v)}><FileText className="h-4 w-4" />{t("forms.preview")}</Button>
                {downloadUrl ? <a href={downloadUrl} target="_blank" rel="noopener" className="inline-flex min-h-[40px] items-center gap-2 rounded-xl border border-ink/15 bg-white px-3 text-sm font-semibold hover:bg-ink/5"><Download className="h-4 w-4" />{t("forms.download")}</a> : null}
                <Button size="sm" variant="ghost" onClick={() => q.refetch()} title="refresh link"><RefreshCw className="h-4 w-4" /></Button>
              </div>
            </Card>
          ) : null}
        </div>
      </div>

      {previewOpen && downloadUrl ? (
        <Card className="overflow-hidden">
          <CardHeader title={t("forms.preview")} action={<Button size="sm" variant="ghost" onClick={() => setPreviewOpen(false)}>{t("common.close")}</Button>} />
          <iframe title={document?.title ?? "preview"} src={downloadUrl} className="w-full h-[80vh] border-t border-ink/10 bg-ink/5" />
        </Card>
      ) : null}
    </div>
  );
}

function document_scroll(path: string) {
  const el = window.document.getElementById(`field-${path}`);
  el?.scrollIntoView({ behavior: "smooth", block: "center" });
  const input = el?.querySelector<HTMLElement>("input,select,textarea");
  input?.focus();
}

function blank(v: unknown): boolean {
  if (v == null) return true;
  if (typeof v === "string") return v.trim() === "" || v === "unknown";
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

const inputCls = "w-full min-h-tap rounded-xl border bg-white px-3 text-base text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";

function FieldEditor({ field: f, value, onChange, prefilled, missing, locale }: { field: FormField; value: unknown; onChange: (v: unknown) => void; prefilled: boolean; missing: boolean; locale: "en" | "es" }) {
  const t = useT();
  const label = (
    <span className="mb-1 flex flex-wrap items-center gap-1 text-sm font-semibold text-ink/80">
      {f.label[locale]}{f.required ? <span className="text-red-600">*</span> : null}
      {prefilled ? <Badge tone="blue" className="normal-case">{t("forms.prefilledFrom")}</Badge> : null}
    </span>
  );
  const border = missing ? "border-amber-400" : "border-ink/15";
  const wrapCls = f.type === "table" || f.type === "textarea" ? "sm:col-span-2" : "";
  if (f.type === "table") {
    const rows = Array.isArray(value) ? (value as Row[]) : [];
    const cols = f.columns ?? [];
    const setRow = (i: number, k: string, v: unknown) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
    return (
      <div id={`field-${f.path}`} className={cn("block", wrapCls)}>
        {label}
        <div className="overflow-x-auto rounded-xl border border-ink/10">
          <table className="w-full text-sm">
            <thead className="bg-surface text-left text-ink/60"><tr>{cols.map((c) => <th key={c.key} className="px-2 py-2 font-semibold">{c.label[locale]}</th>)}<th /></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-ink/5">
                  {cols.map((c) => (
                    <td key={c.key} className="px-1 py-1 min-w-[120px]">
                      {c.type === "select" ? (
                        <select className={cn(inputCls, "min-h-[36px] border-ink/15 text-sm")} value={String(r[c.key] ?? "")} onChange={(e) => setRow(i, c.key, e.target.value)}>
                          <option value="">{t("forms.pickOne")}</option>
                          {c.options?.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
                        </select>
                      ) : (
                        <input className={cn(inputCls, "min-h-[36px] border-ink/15 text-sm")} type={c.type === "number" || c.type === "money" ? "number" : c.type === "date" ? "date" : "text"} value={r[c.key] == null ? "" : String(r[c.key])} onChange={(e) => setRow(i, c.key, c.type === "number" || c.type === "money" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />
                      )}
                    </td>
                  ))}
                  <td className="px-1"><button type="button" aria-label={t("forms.removeRow")} className="min-h-[36px] min-w-[36px] inline-flex items-center justify-center rounded-lg text-ink/50 hover:bg-red-50 hover:text-red-700" onClick={() => onChange(rows.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
              {rows.length === 0 ? <tr><td colSpan={cols.length + 1} className="px-3 py-2 text-ink/50">{missing ? t("forms.unknown") : "—"}</td></tr> : null}
            </tbody>
          </table>
        </div>
        <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => onChange([...rows, {}])}><Plus className="h-4 w-4" />{t("forms.addRow")}</Button>
      </div>
    );
  }
  const str = value == null ? "" : String(value);
  return (
    <label id={`field-${f.path}`} className={cn("block", wrapCls)}>
      {label}
      {f.type === "textarea" ? <textarea className={cn(inputCls, border, "min-h-[88px] p-3")} value={str} onChange={(e) => onChange(e.target.value)} /> : null}
      {f.type === "select" ? (
        <select className={cn(inputCls, border)} value={str} onChange={(e) => onChange(e.target.value)}>
          <option value="">{t("forms.pickOne")}</option>
          {f.options?.map((o) => <option key={o} value={o}>{f.path.startsWith("signature.method") ? t(`forms.signature.${o}`) : o === "unknown" ? t("forms.unknown") : o.replace(/_/g, " ")}</option>)}
        </select>
      ) : null}
      {f.type === "boolean" ? (
        <select className={cn(inputCls, border)} value={value === true ? "yes" : value === false ? "no" : ""} onChange={(e) => onChange(e.target.value === "yes" ? true : e.target.value === "no" ? false : "")}>
          <option value="">{t("forms.unknown")}</option><option value="yes">{t("common.yes")}</option><option value="no">{t("common.no")}</option>
        </select>
      ) : null}
      {f.type === "text" || f.type === "number" || f.type === "money" || f.type === "date" ? (
        <input className={cn(inputCls, border)} type={f.type === "number" || f.type === "money" ? "number" : f.type === "date" ? "date" : "text"} value={str} inputMode={f.type === "money" || f.type === "number" ? "decimal" : undefined} onChange={(e) => onChange(f.type === "number" || f.type === "money" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />
      ) : null}
      {f.help ? <span className="mt-1 block text-xs text-ink/50">{f.help[locale]}</span> : null}
    </label>
  );
}
